/* eslint-disable react/jsx-no-bind */
import React, { useState, useCallback, useEffect, useReducer } from 'react';
import { Modal, Icon, TextField, Spinner, ResourceList, ResourceItem, Thumbnail, TextStyle } from '@shopify/polaris';
import { SearchMajor } from '@shopify/polaris-icons';
import { getProductsRes } from './optimize-campaign';
import { reducerActions, resourcePickerReducers } from './resourcePickerReducers';
import './ShopifyResourcePicker.scss';

export default function ResourceDataPickerProduct() {
  const [state, dispatch] = useReducer(reducerActions, resourcePickerReducers);
  const [, setAction] = useState(0);
  const [typing] = useState({
    timeoutScroll: 0,
    timeoutTyping: 0,
    first: 25,
    search: '',
    after: '',
    dataNodes: {}
  });
  const [countIndex, setCountIndex] = useState(0);
  const [next, setNext] = useState(1);
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [firstRes, setFirstRes] = useState(false);
  const [search, setSearch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [listItems, setListItems] = useState([]);
  const [newListItemShows, setNewListItemShows] = useState([]);
  const [pageInfo, setPageInfo] = useState({
    hasPreviousPage: false,
    startCursor: '',
    hasNextPage: false,
    endCursor: ''
  });

  // eslint-disable-next-line
  const onSelectionChange = useCallback(setSelectedItems, []);

  const onScrolledToBottom = useCallback(() => {
    if (!loading && !searchLoading && pageInfo.hasNextPage) {
      typing.after = pageInfo.endCursor;
      setLoading(true);

      clearTimeout(typing.timeoutScroll);
      typing.timeoutScroll = setTimeout(() => setNext(v => v + 1), 300);
    }
    // eslint-disable-next-line
  }, [loading, searchLoading, pageInfo]);

  const closePopup = useCallback(() => {
    dispatch({ type: 'setOpenProductPicker', payload: false });
    setLoading(false);
    setSearchLoading(false);
    setSelectedItems([]);
  }, []);

  const addItems = () => {
    const selectedMap = {};
    selectedItems.forEach(key => {
      selectedMap[key] = typing.dataNodes[key];
    });
    dispatch({ type: 'onSelected', payload: selectedMap });
    closePopup();
  };

  function renderItem(item) {
    const { id, title, src, countIndex } = item;
    return (
      <div key={id} className='resource-list--item-picker' style={{ marginTop: `${65 * countIndex}px` }}>
        <ResourceItem
          id={id}
          title={title}
          media={
            src ? (
              <Thumbnail source={src} alt={title} size='small' />
            ) : (
              <div className='Polaris-Thumbnail Polaris-Thumbnail--sizeSmall'>
                <div className='picker-item__image--svg'>
                  <svg viewBox='0 0 20 20'>
                    <path fill='currentColor' d='M14 9l-5 5-3-2-5 3v4h18v-6z' />
                    <path d='M19 0H1a1 1 0 0 0-1 1v18a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V1a1 1 0 0 0-1-1zM8 6c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm0 4c1.654 0 3-1.346 3-3S9.654 4 8 4 5 5.346 5 7s1.346 3 3 3zm-6 8v-2.434l3.972-2.383 2.473 1.65c.398.264.925.21 1.262-.126l4.367-4.367L18 13.48V18H2zM18 2v8.92l-3.375-2.7a.999.999 0 0 0-1.332.073l-4.42 4.42-2.318-1.545a1 1 0 0 0-1.07-.025L2 13.233V2h16z' />
                  </svg>
                </div>
              </div>
            )
          }
        >
          <TextStyle>
            <div
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '400px'
              }}
              title={title}
            >
              {title}
            </div>
          </TextStyle>
        </ResourceItem>
      </div>
    );
  }

  function resp(res = { data: { products: {} } }) {
    if (!firstRes) setFirstRes(true);
    setLoading(false);
    setSearchLoading(false);
    if (res?.data?.products) {
      const { pageInfo: pageInfoData, nodes } = res.data.products;
      if (pageInfoData) setPageInfo(pageInfoData);
      if (nodes) {
        let newCountIndex = countIndex;
        const dataNewNodes = [];
        nodes.forEach(({ id, title, handle, featuredMedia }) => {
          id = id.match(/[0-9]+/).join('');
          if (!typing.dataNodes[id]) {
            let src = featuredMedia?.preview?.image?.url || '';
            if (src) src += '&width=60';
            const item = {
              id,
              title,
              handle,
              badge: 'Product',
              pickerType: 'products',
              publicPath: 'products',
              src,
              countIndex: newCountIndex
            };
            typing.dataNodes[id] = item;
            dataNewNodes.push(item);
            newCountIndex++;
          }
        });
        setCountIndex(newCountIndex);
        if (dataNewNodes.length > 0) {
          setListItems(v => [...v, ...dataNewNodes]);
          setNewListItemShows(v => (v.length > 0 ? v : dataNewNodes.slice(0, 10)));
        }
      }
    }
  }

  useEffect(() => {
    if (open && next > 0) {
      getProductsRes(typing.first, typing.search, typing.after).then(resp);
    }
    // eslint-disable-next-line
  }, [open, next]);

  useEffect(() => {
    if (open && firstRes && search !== null) {
      setNext(0);
      typing.after = '';
      typing.search = search;
      setCountIndex(0);
      clearTimeout(typing.timeoutTyping);
      typing.timeoutTyping = setTimeout(() => {
        typing.dataNodes = {};
        setListItems([]);
        setNewListItemShows([]);
        setSearchLoading(true);
        getProductsRes(typing.first, typing.search, typing.after).then(resp);
      }, 1000);
    }
    // eslint-disable-next-line
  }, [open, firstRes, search]);

  useEffect(() => {
    if (listItems.length > 0)
      listItems.forEach((item, index) => {
        item.countIndex = index;
      });
  }, [listItems]);

  useEffect(() => {
    if (state.openProductPicker && !open) setOpen(true);
    if (state.openProductPicker) {
      setTimeout(() => {
        setFocused(true);
        setNewListItemShows(listItems.slice(0, 10));
      }, 200);
    } else {
      setFocused(false);
    }
    // eslint-disable-next-line
  }, [state.openProductPicker]);

  useEffect(() => {
    dispatch({
      type: 'setActionResourceDataPickerProduct',
      payload: setAction
    });
  }, []);

  return (
    <Modal
      open={state.openProductPicker}
      title='Add products'
      // onScrolledToBottom={onScrolledToBottom}
      noScroll
      onClose={closePopup}
      primaryAction={{
        content: 'Add',
        onAction: addItems,
        disabled: selectedItems.length === 0
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: closePopup,
          disabled: false
        }
      ]}
      footer={`${selectedItems.length} product${selectedItems.length > 1 ? 's' : ''} selected`}
    >
      <Modal.Section key='search'>
        <TextField
          label=''
          focused={focused}
          labelHidden
          clearButton
          onClearButtonClick={() => {
            if (!searchLoading && !loading) setSearch('');
          }}
          prefix={searchLoading ? <Spinner size='small' /> : <Icon source={SearchMajor} color='base' />}
          value={search || ''}
          onChange={value => {
            if ((!searchLoading && !loading) || !firstRes) setSearch(value);
          }}
          placeholder='Search products'
          autoComplete=''
        />
      </Modal.Section>
      <div
        className='modal-resource-picker-graphql'
        style={{ height: `${listItems.length * 65}px` }}
        onScroll={({ target }) => {
          const { scrollTop, offsetHeight } = target;
          const newList = listItems.filter(item => {
            return item.countIndex * 65 + 65 >= scrollTop && item.countIndex * 65 <= scrollTop + offsetHeight;
          });
          setNewListItemShows(newList);
          if (scrollTop + offsetHeight > listItems.length * 65) onScrolledToBottom();
        }}
      >
        <ResourceList
          loading={loading}
          items={newListItemShows}
          renderItem={renderItem}
          selectedItems={selectedItems}
          onSelectionChange={onSelectionChange}
          selectable
        />
        <div
          style={{
            position: 'absolute',
            marginTop: `${listItems.length * 65}px`
          }}
        >
          &nbsp;
        </div>
      </div>
    </Modal>
  );
}
